// webapp/frontend/src/store/modules/snapshots.js

import api from '@/services/api'

const DATASETS = ['dev-items', 'subsystems', 'actions', 'users']

function emptyDatasetState() {
  return {
    itemsById: {},
    orderedIds: [],
    etag: null,
    version: 0,
    lastFetchedAt: null,
  }
}

const state = () => ({
  datasets: {
    'dev-items': emptyDatasetState(),
    'subsystems': emptyDatasetState(),
    'actions': emptyDatasetState(),
    'users': emptyDatasetState(),
  }
})

const getters = {
  dataset: (state) => (name) => state.datasets[name] || emptyDatasetState(),
  list: (state) => (name) => {
    const ds = state.datasets[name] || emptyDatasetState()
    return ds.orderedIds.map(id => ds.itemsById[id]).filter(Boolean)
  },
  getById: (state) => (name, id) => {
    const ds = state.datasets[name] || emptyDatasetState()
    return ds.itemsById[id] || null
  },
}

const mutations = {
  CLEAR_ALL(state) {
    console.log('[snapshots] CLEAR_ALL mutation')
    for (const name of DATASETS) {
      state.datasets[name] = emptyDatasetState()
    }
  },
  SET_DATASET(state, { name, items, etag, version }) {
    console.log(`[snapshots] SET_DATASET ${name} count=${(items||[]).length} ver=${version}`)
    const ds = state.datasets[name] || emptyDatasetState()
    const byId = {}
    const ids = []
    for (const item of items || []) {
      if (!item || typeof item.id === 'undefined') continue
      byId[item.id] = item
      ids.push(item.id)
    }
    ds.itemsById = byId
    ds.orderedIds = ids
    ds.etag = etag || ds.etag
    ds.version = typeof version === 'number' ? version : ds.version
    ds.lastFetchedAt = new Date().toISOString()
    state.datasets = { ...state.datasets, [name]: ds }
  },
  APPLY_DIFF(state, { name, changed = [], deleted = [], toVersion, etag }) {
    const ds = state.datasets[name] || emptyDatasetState()
    // apply changed
    for (const item of changed) {
      if (!item || typeof item.id === 'undefined') continue
      ds.itemsById[item.id] = item
      if (!ds.orderedIds.includes(item.id)) ds.orderedIds.push(item.id)
    }
    // apply deleted
    if (Array.isArray(deleted) && deleted.length) {
      for (const id of deleted) {
        delete ds.itemsById[id]
      }
      ds.orderedIds = ds.orderedIds.filter(id => !deleted.includes(id))
    }
    if (typeof toVersion === 'number') ds.version = toVersion
    if (etag) ds.etag = etag
    ds.lastFetchedAt = new Date().toISOString()
    state.datasets = { ...state.datasets, [name]: ds }
  },
}

const actions = {
  async clearAll({ commit }) {
    commit('CLEAR_ALL')
  },

  async ensureDataset({ state, commit }, name) {
    const ds = state.datasets[name] || emptyDatasetState()
    // Always force fetch if version is 0 (cleared state)
    const requestEtag = ds.version === 0 ? null : ds.etag
    try {
      console.log(`[snapshots] ensureDataset ${name} current_ver=${ds.version} sending_etag=${requestEtag}`)
      const resp = await api.getSnapshotRaw(name, requestEtag)
      if (resp.status === 304) {
        console.log(`[snapshots] ensureDataset ${name} returned 304 (not modified)`)
        return { updated: false }
      }
      const newEtag = resp.headers?.etag || null
      const body = resp.data || {}
      commit('SET_DATASET', { name, items: body.items || body, etag: newEtag, version: body.version })
      return { updated: true, etag: newEtag }
    } catch (e) {
      // surface but don't break others
      // eslint-disable-next-line no-console
      console.warn(`[snapshots] ensureDataset(${name}) failed`, e)
      return { updated: false, error: e }
    }
  },

  async refreshAllInParallel({ dispatch }) {
    console.log('[snapshots] refreshAllInParallel started')
    const tasks = DATASETS.map(d => dispatch('ensureDataset', d))
    const results = await Promise.allSettled(tasks)
    console.log('[snapshots] refreshAllInParallel done', results.map(r => r.status))
    return results
  },

  async applyPushMessage({ state, commit, dispatch }, msg) {
    let name = msg?.dataset
    // Normalize backend dataset names to store keys (dev_items -> dev-items)
    if (name === 'dev_items') name = 'dev-items'
    
    const toVersion = msg?.to_version
    if (!name || typeof toVersion !== 'number') return
    const ds = state.datasets[name] || emptyDatasetState()
    // If we have embedded rows, apply directly; else fetch diff
    if (Array.isArray(msg.changed_rows) && msg.changed_rows.length) {
      commit('APPLY_DIFF', { name, changed: msg.changed_rows, deleted: msg.deleted_ids || [], toVersion })
      return
    }
    // fetch diff
    try {
      const diffResp = await api.getDiffRaw(name, ds.version)
      const diff = diffResp.data || {}
      commit('APPLY_DIFF', { name, changed: diff.changed || [], deleted: diff.deleted || [], toVersion: diff.to_version, etag: diff.etag })
    } catch (e) {
      // fallback: refetch snapshot for this dataset
      // eslint-disable-next-line no-console
      console.warn(`[snapshots] diff failed for ${name}, refetching snapshot`, e)
      await dispatch('ensureDataset', name)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
